import { useState } from 'react';
import { X, ArrowRight, Zap, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PLAN_NAMES = { free: 'Free', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };
const PLAN_COLORS = {
  free: 'text-gray-400', starter: 'text-cyan-400', growth: 'text-indigo-400', enterprise: 'text-amber-400',
};

/**
 * UpgradePrompt — shown when a user hits a plan-gated feature.
 * Accepts the structured 403 error from the backend.
 */
export const UpgradePrompt = ({ error, onClose, onUpgrade }) => {
  if (!error || error.error !== 'plan_required') return null;

  const requiredName = PLAN_NAMES[error.required_plan] || error.required_plan;
  const currentName = PLAN_NAMES[error.current_plan] || error.current_plan;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
        className="p-4 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20"
        data-testid="upgrade-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5 text-indigo-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-medium text-sm">{error.feature || 'Feature'} — Upgrade Required</h4>
            <p className="text-xs text-gray-400 mt-1">{error.message}</p>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs text-gray-500">
                Current: <span className={PLAN_COLORS[error.current_plan]}>{currentName}</span>
              </span>
              <ArrowRight className="w-3 h-3 text-gray-600" />
              <span className="text-xs text-gray-500">
                Required: <span className={PLAN_COLORS[error.required_plan]}>{requiredName}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => onUpgrade ? onUpgrade(error.required_plan) : null}
                className="px-4 py-1.5 rounded-lg bg-indigo-500 text-white text-xs font-medium hover:bg-indigo-400 transition-colors flex items-center gap-1.5"
                data-testid="upgrade-prompt-btn"
              >
                <Zap className="w-3 h-3" /> Upgrade to {requiredName}
              </button>
              {onClose && (
                <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-400 text-xs hover:bg-white/10 transition-colors">
                  Maybe Later
                </button>
              )}
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * FeatureGate — wraps a feature section. Shows lock overlay when plan is insufficient.
 */
export const FeatureGate = ({ feature, allowed, requiredPlan, currentPlan, children, onUpgrade }) => {
  const [dismissed, setDismissed] = useState(false);

  if (allowed || dismissed) return children;

  const requiredName = PLAN_NAMES[requiredPlan] || requiredPlan;

  return (
    <div className="relative" data-testid={`feature-gate-${feature}`}>
      <div className="opacity-40 pointer-events-none select-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-[2px] rounded-2xl">
        <div className="text-center px-6">
          <Lock className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
          <p className="text-white text-sm font-medium">{requiredName} Plan Required</p>
          <p className="text-xs text-gray-400 mt-1 max-w-xs">Upgrade to unlock {feature}</p>
          <button
            onClick={() => onUpgrade ? onUpgrade(requiredPlan) : null}
            className="mt-3 px-4 py-1.5 rounded-lg bg-indigo-500 text-white text-xs font-medium hover:bg-indigo-400 transition-colors inline-flex items-center gap-1.5"
            data-testid={`feature-gate-upgrade-${feature}`}
          >
            <Zap className="w-3 h-3" /> Upgrade
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * PlanBadge — small inline badge showing the plan level.
 */
export const PlanBadge = ({ plan }) => {
  const colors = {
    free: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    starter: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    growth: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    enterprise: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${colors[plan] || colors.free}`} data-testid="plan-badge">
      {(PLAN_NAMES[plan] || plan).toUpperCase()}
    </span>
  );
};

/**
 * UsageBar — progress bar showing usage against plan limit.
 */
export const UsageBar = ({ label, current, limit, unit = '' }) => {
  const isUnlimited = limit === 'unlimited' || limit === -1;
  const pct = isUnlimited ? 0 : limit > 0 ? Math.min(100, Math.round((current / limit) * 100)) : 0;
  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-indigo-500';

  return (
    <div className="space-y-1" data-testid={`usage-bar-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-300">
          {current}{unit} / {isUnlimited ? 'Unlimited' : `${limit}${unit}`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${isUnlimited ? 5 : pct}%` }} />
      </div>
    </div>
  );
};

export default UpgradePrompt;
